import { tenants } from '../models/tenants';
import { users } from '../models/users';
import { Tenant } from '../types/Tenant';
import { BaseRepository } from '../../../classes/BaseRepository';
import { retryableQuery } from '../../../functions/retryableQuery';
import { db } from '../../../db';
import { eq, inArray } from 'drizzle-orm';
import { AppError, AsyncResult, err, ok } from '@shared/utils';

/**
 * @group Tenant
 * @summary Tenant repository
 * 
 * <blockquote>
 * Provides data access methods for tenant management, including CRUD operations and tenant-based permission checks.
 * Extends BaseRepository to inherit common CRUD operations while adding domain-specific query methods.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const tenantRepository = new TenantRepository();
 * const tenant = await tenantRepository.getTenantById('123');
 * ```
 * 
 */
export class TenantRepository extends BaseRepository<Tenant> {
  protected table = tenants;

  /**
   * Get tenant by name
   * 
   * @param name - The tenant name to search for
   * @returns AsyncResult containing the tenant or error
   */
  async getTenantByName(name: string): AsyncResult<Tenant | null, AppError> {
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select().from(this.table).where(eq(this.table.name, name))
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return ok(null);
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }


  /**
   * Get all tenants with the owning user's email
   * 
   * @returns AsyncResult containing tenants with userEmail field
   */
  async getAllWithUserEmail(): AsyncResult<(Tenant & { userEmail: string | null })[], AppError> {
    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({
              id: tenants.id,
              external_id: tenants.external_id,
              name: tenants.name,
              user_id: tenants.user_id,
              date_created: tenants.date_created,
              date_modified: tenants.date_modified,
              userEmail: users.email,
            })
            .from(tenants)
            .leftJoin(users, eq(tenants.user_id, users.id))
        );

        return queryResult.match({
          ok: (rows) => ok(rows as (Tenant & { userEmail: string | null })[]),
          err: (error) => err(error),
        });
      },
      err: async (error) => err(error),
    });
  }

  /**
   * Get tenants by IDs with the owning user's email
   * 
   * @param tenantIds - Array of tenant IDs to fetch
   * @returns AsyncResult containing tenants with userEmail field
   */
  async getTenantsByIdsWithUserEmail(tenantIds: string[]): AsyncResult<(Tenant & { userEmail: string | null })[], AppError> {
    if (!tenantIds || tenantIds.length === 0) {
      return ok([]);
    }

    return db.match({
      ok: async (database) => {
        const queryResult = await retryableQuery(() =>
          database
            .select({
              id: tenants.id,
              external_id: tenants.external_id,
              name: tenants.name,
              user_id: tenants.user_id,
              date_created: tenants.date_created,
              date_modified: tenants.date_modified,
              userEmail: users.email,
            })
            .from(tenants)
            .leftJoin(users, eq(tenants.user_id, users.id))
            .where(inArray(tenants.id, tenantIds))
        );

        return queryResult.match({
          ok: (rows) => ok(rows as (Tenant & { userEmail: string | null })[]),
          err: (error) => err(error),
        });
      },
      err: async (error) => err(error),
    });
  }
} 